import { apiRequest } from "@data/redux/actions/api"
import { tutorMonthlyScoreService } from "@data/services/tutorMonthlyScoreService"
import { tutorsService } from "@data/services/tutorsService"
import { getFromStorage, setToStorage } from "@data/utility/asyncStorageUtils"
import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useApiStatus } from "./useApiStatus"

const TUTOR_SCORE_CACHE_KEY = "tutorScoreCache"
const ALL_SCORES_CACHE_KEY = "allScoresCache"

export function useTutorScore(
    userId,
    forceRefresh = false,
    scoreType = "GLOBAL",
    // La boîte « Erreur » de l'ancien kit sur un échec ; faux sous l'espace tuteur du design system.
    errorMessage = true
) {
    const dispatch = useDispatch()
    const [shouldFetch, setShouldFetch] = useState(false)

    const isAll = scoreType === "ALL"

    const service = isAll
        ? tutorMonthlyScoreService.getAllScores
        : tutorsService.getTutorScore

    const cacheKey = isAll
        ? ALL_SCORES_CACHE_KEY
        : TUTOR_SCORE_CACHE_KEY

    const apiKey = isAll
        ? "getAllScores"
        : "getTutorScore"

    // Get request status from Redux
    const isLoading = useSelector((state) => !!state.api[apiKey]?.request)

    // Check if data is from a different day
    const isFromDifferentDay = (cachedTimestamp) => {
        const cachedDate = new Date(parseInt(cachedTimestamp))
        const today = new Date()
        return cachedDate.toDateString() !== today.toDateString()
    }

    // Check cache validity on mount
    useEffect(() => {
        const checkCache = async () => {
            if (!userId) return

            try {
                const cachedData = await getFromStorage(cacheKey + "_" + userId)

                // For all scores, always fetch fresh to get ranking
                if (isAll) {
                    setShouldFetch(true)
                } else if (
                    forceRefresh ||
                    !cachedData ||
                    isFromDifferentDay(cachedData)
                ) {
                    setShouldFetch(true)
                }
            } catch (error) {
                console.warn(`Error checking ${scoreType} score cache:`, error)
                setShouldFetch(true)
            }
        }

        checkCache()
    }, [userId, forceRefresh])

    // Handle API success and cache the result
    useApiStatus(service, null, errorMessage, async () => {
        try {
            await setToStorage(
                cacheKey + "_" + userId,
                new Date().getTime().toString()
            )
            setShouldFetch(false)
        } catch (error) {
            console.warn(`Error caching ${scoreType} score:`, error)
        }
    })

    // Manual fetch function
    const fetchTutorScore = () => {
        if (userId) {
            dispatch(apiRequest(service))
        }
    }

    return {
        shouldFetch,
        fetchTutorScore,
        isLoading,
    }
}
